import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Slider from 'react-slick';
import { FaCampground, FaHotel } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import useScrollToTop from '../ScrollToTop';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './place.css';

const Samburu = () => {
  useScrollToTop();
  const { t } = useTranslation('samburu');

  const activities = t('topActivities.list', { returnObjects: true });
  const tips = t('travelTips.tips', { returnObjects: true });
  const options = t('accommodation.options', { returnObjects: true });

  return (
    <motion.div
      className="place-page"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: 'easeOut' }}
    >
      {/* SEO */}
      <Helmet>
  <title>Maasai Mara Safari from Nairobi | Budget & Luxury Tours - Olenini Safaris</title>
  <meta name="description" content="Book your unforgettable Maasai Mara safari from Nairobi with Olenini Safaris. Tailor-made budget to luxury safari packages with game drives, Big Five sightings, and local experiences." />
  <meta name="keywords" content="Maasai Mara safari from Nairobi, budget safari Kenya, Maasai Mara tours, Big Five Kenya, 3-day safari Maasai Mara, Kenya safari packages" />
  <meta name="robots" content="index, follow" />
  <link rel="canonical" href="https://oleninisafaris.com/places/mara" />

  {/* Open Graph / Facebook */}
  <meta property="og:title" content="Maasai Mara Safari from Nairobi | Olenini Safaris" />
  <meta property="og:description" content="Tailor-made safaris to Maasai Mara from Nairobi. Budget, mid-range, and luxury packages available. Experience Kenya's iconic wildlife." />
  <meta property="og:image" content="https://oleninisafaris.com/images/ffleetmuddy.jpeg" />
  <meta property="og:url" content="https://oleninisafaris.com/places/mara" />
  <meta property="og:type" content="article" />

  {/* Twitter */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Maasai Mara Safari from Nairobi | Olenini Safaris" />
  <meta name="twitter:description" content="Unforgettable safari tours to Maasai Mara starting from Nairobi. Book your 3-day or custom Kenya safari today." />
  <meta name="twitter:image" content="https://oleninisafaris.com/images/ffleetmuddy.jpeg" />

  {/**AI READINGS */}

<script type="application/ld+json">
{`
{
  "@context": "https://schema.org",
  "@type": "TouristTrip",
  "name": "Maasai Mara Safari from Nairobi",
  "description": "3-day Maasai Mara safari starting from Nairobi. Includes transport, game drives, park entry, accommodation and meals.",
  "touristType": ["Budget", "Mid-range", "Luxury"],
  "itinerary": {
    "@type": "ItemList",
    "itemListElement": [
      {
        "@type": "TouristAttraction",
        "name": "Maasai Mara National Reserve",
        "url": "https://oleninisafaris.com/places/mara"
      }
    ]
  },
  "provider": {
    "@type": "TravelAgency",
    "name": "Olenini Tours & Safaris",
    "url": "https://oleninisafaris.com",
    "telephone": "+254702965315"
  },
  "offers": {
    "@type": "Offer",
    "priceCurrency": "USD",
    "price": "350",
    "url": "https://oleninisafaris.com/places/mara",
    "availability": "https://schema.org/InStock",
    "validFrom": "2025-10-01"
  }
}
`}
</script>


</Helmet>


      {/* Hero */}
      <section className="place-hero">
        <img
          src="/images/samburuzebras.jpg"
          alt="Zebras grazing in the Maasai Mara savannah"
          className="hero-image"
        />
        <div className="hero-text">
          <h1>{t('heroText.title')}</h1>
          <p>{t('heroText.description')}</p>
        </div>
      </section>

      {/* About */}
      <section className="place-about">
        <h2>{t('placeAbout.title')}</h2>
        <p dangerouslySetInnerHTML={{ __html: t('placeAbout.description') }} />
      </section>

      {/* Wildlife */}
      <section className="place-highlights">
        <div className="card">
          <h3>{t('wildlifeHighlights.title')}</h3>
          <p dangerouslySetInnerHTML={{ __html: t('wildlifeHighlights.paragraph1') }} />
          <p dangerouslySetInnerHTML={{ __html: t('wildlifeHighlights.paragraph2') }} />
          <p dangerouslySetInnerHTML={{ __html: t('wildlifeHighlights.paragraph3') }} />

          <h3>{t('topActivities.title')}</h3>
          <ul>
            {activities.map((item, index) => (
              <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
            ))}
          </ul>
        </div>
      </section>

      {/* Carousel + Tips */}
      <section className="place-highlights alt-layout">
        <div className="card unified-card">
          <div className="image-carousel">
            <Slider
              autoplay
              autoplaySpeed={3000}
              infinite
              slidesToShow={1}
              slidesToScroll={1}
              arrows={false}
              dots={false}
              pauseOnHover
              swipeToSlide
            >
              <div>
                <img
                  src="/images/samburubeests.jpg"
                  alt="Wildebeest running during the Great Migration"
                  className="slick-image"
                  loading="lazy"
                />
              </div>
              <div>
                <img
                  src="/images/samburuele.jpg"
                  alt="Crocodile-infested Mara River landscape"
                  className="slick-image"
                  loading="lazy"
                />
              </div>
              <div>
                <img
                  src="/images/samburugiraffe.jpg"
                  alt="Expansive view of Maasai Mara plains"
                  className="slick-image"
                  loading="lazy"
                />
              </div>
              <div>
                <img
                  src="/images/samburzeb.jpg"
                  alt="Elephant walking through the grassland"
                  className="slick-image"
                  loading="lazy"
                />
              </div>
            </Slider>
          </div>

          <div className="text-content">
            <h3>{t('travelTips.title')}</h3>
            <p>{t('travelTips.intro')}</p>
            <ul>
              {tips.map((tip, index) => (
                <li key={index} dangerouslySetInnerHTML={{ __html: tip }} />
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="place-gallery">
        <h2>{t('gallery.title')}</h2>
        <div className="gallery-grid">
          <img src="/images/sambur.jpg" alt="Lion resting in the Maasai Mara" loading="lazy" />
          <img src="/images/samburuzebraG.jpg" alt="Leopard hidden in tree in Mara" loading="lazy" />
          <img src="/images/headup.jpg" alt="Hyena roaming in the grasslands" loading="lazy" />
        </div>
      </section>

      {/* Accommodation */}
      <section className="place-accommodation">
        <h2>{t('accommodation.title')}</h2>
        <p>{t('accommodation.intro')}</p>

        <div className="accommodation-options">
          {options.map((option, index) => (
            <div key={index} className="option-card">
              {index === 0 && <FaCampground className="icon" />}
              {index === 1 && <FaHotel className="icon" />}
              <h3>{option.title}</h3>
              <p>{option.description}</p>
            </div>
          ))}
        </div>

        <p className="note">{t('accommodation.note')}</p>
      </section>

      {/* CTA */}
      <section className="place-cta" aria-label="Call to Action Section">
        <h2>{t('cta.title')}</h2>
        <p>{t('cta.description')}</p>
        <Link to="/contact?source=maasaimaradestination" className="book-now">
          {t('cta.button')}
        </Link>
      </section>
    </motion.div>
  );
};

export default Samburu;
